import React, { useContext, useEffect, useMemo, useState } from "react";
import { Avatar, Box, Typography } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { DraftYearContext } from "../../contexts/DraftYearContext";
import { publicApi } from "../../api/http";
import PageLoader from "../../components/PageLoader/PageLoader";
import DraftHubPlayerPanel from "./DraftHubPlayerPanel";
import DraftHubRightPanel from "./DraftHubRightPanel";
import "./DraftHub.css";

const SORT_MODES = {
    CONSENSUS: "consensus",
    DRAFT_ORDER: "draft-order",
};

function pickAndRoundToOverall(pick, round, year) {
    let teamCount = 30;
    if (year >= 2021) {
        teamCount = 32;
    } else if (year >= 2017) {
        teamCount = 31;
    }
    return pick + (round - 1) * teamCount;
}

function sortByConsensus(players) {
    return [...players].sort((a, b) => {
        const rankA = a.consensus_ranking ?? Number.MAX_SAFE_INTEGER;
        const rankB = b.consensus_ranking ?? Number.MAX_SAFE_INTEGER;
        if (rankA !== rankB) return rankA - rankB;
        return `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`);
    });
}

function getDraftOverall(player, draftYear) {
    const draftResults = player.draft_results;
    if (!draftResults) return Number.MAX_SAFE_INTEGER;
    return pickAndRoundToOverall(
        draftResults.pick,
        draftResults.round,
        draftResults.draft_year ?? draftYear,
    );
}

function sortByDraftOrder(players, draftYear) {
    return [...players].sort((a, b) => {
        const pickA = getDraftOverall(a, draftYear);
        const pickB = getDraftOverall(b, draftYear);
        if (pickA !== pickB) return pickA - pickB;
        return `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`);
    });
}

function sortPlayers(players, sortMode, draftYear, draftCompleted) {
    if (draftCompleted && sortMode === SORT_MODES.DRAFT_ORDER) {
        return sortByDraftOrder(players, draftYear);
    }
    return sortByConsensus(players);
}

function getListRank(player, sortMode, draftYear, draftCompleted) {
    if (draftCompleted && sortMode === SORT_MODES.DRAFT_ORDER) {
        const draftResults = player.draft_results;
        if (!draftResults) return "—";
        return pickAndRoundToOverall(
            draftResults.pick,
            draftResults.round,
            draftResults.draft_year ?? draftYear,
        );
    }
    return player.consensus_ranking ?? "—";
}

function DraftHub() {
    const { draftYear } = useContext(DraftYearContext);
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPlayerId, setSelectedPlayerId] = useState(null);
    const [selectedPlayerData, setSelectedPlayerData] = useState(null);
    const [loadingPlayer, setLoadingPlayer] = useState(false);
    const [sortMode, setSortMode] = useState(SORT_MODES.CONSENSUS);

    const draftCompleted = useMemo(
        () => players.some((player) => player.draft_results),
        [players],
    );

    const sortedPlayers = useMemo(
        () => sortPlayers(players, sortMode, draftYear, draftCompleted),
        [players, sortMode, draftYear, draftCompleted],
    );

    useEffect(() => {
        setLoading(true);
        publicApi.get("/players", { params: { draft_year: draftYear } })
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : [];
                const completed = list.some((player) => player.draft_results);
                const defaultSort = completed ? SORT_MODES.DRAFT_ORDER : SORT_MODES.CONSENSUS;
                const sorted = sortPlayers(list, defaultSort, draftYear, completed);
                setPlayers(list);
                setSortMode(defaultSort);
                setSelectedPlayerId((prev) => {
                    if (prev && list.some((p) => p.player_id === prev)) return prev;
                    return sorted[0]?.player_id || null;
                });
            })
            .catch(() => {
                setPlayers([]);
                setSelectedPlayerId(null);
            })
            .finally(() => setLoading(false));
    }, [draftYear]);

    useEffect(() => {
        if (!selectedPlayerId) {
            setSelectedPlayerData(null);
            return;
        }

        const cached = players.find((p) => p.player_id === selectedPlayerId);
        if (cached) setSelectedPlayerData(cached);

        setLoadingPlayer(true);
        publicApi.get(`/players/${selectedPlayerId}`)
            .then((res) => setSelectedPlayerData(res.data))
            .catch(() => setSelectedPlayerData(cached || null))
            .finally(() => setLoadingPlayer(false));
    }, [selectedPlayerId, players]);

    return (
        <>
            <Helmet>
                <title>Draft Hub - Hockey Pipeline</title>
                <meta
                    name="description"
                    content="Browse consensus-ranked prospects, view player details, discussion, and media rankings in one place."
                />
                <link rel="canonical" href="https://hockeypipeline.com/draft-hub" />
            </Helmet>

            <PageLoader loading={loading} />

            <Box className="draft-hub-page">
                <Box className="draft-hub-layout">
                    <Box className="draft-hub-column draft-hub-column--list">
                        <Box className="draft-hub-list-header">
                            <Typography component="div" className="draft-hub-column-title">
                                {draftCompleted && sortMode === SORT_MODES.DRAFT_ORDER
                                    ? "Draft Order"
                                    : "Consensus Rankings"}
                            </Typography>
                            {draftCompleted && (
                                <Box className="draft-hub-sort-toggle" role="group" aria-label="Sort players">
                                    <button
                                        type="button"
                                        className={[
                                            "draft-hub-sort-option",
                                            sortMode === SORT_MODES.DRAFT_ORDER ? "draft-hub-sort-option--active" : "",
                                        ].filter(Boolean).join(" ")}
                                        onClick={() => setSortMode(SORT_MODES.DRAFT_ORDER)}
                                    >
                                        Draft
                                    </button>
                                    <button
                                        type="button"
                                        className={[
                                            "draft-hub-sort-option",
                                            sortMode === SORT_MODES.CONSENSUS ? "draft-hub-sort-option--active" : "",
                                        ].filter(Boolean).join(" ")}
                                        onClick={() => setSortMode(SORT_MODES.CONSENSUS)}
                                    >
                                        Consensus
                                    </button>
                                </Box>
                            )}
                        </Box>
                        <Box className="draft-hub-player-list">
                            {!loading && sortedPlayers.length === 0 && (
                                <Box className="draft-hub-empty">No players found for this draft year.</Box>
                            )}
                            {sortedPlayers.map((player) => {
                                const isActive = player.player_id === selectedPlayerId;
                                const draftTeamLogo = player.draft_results?.team?.team_logo_url;
                                const showDraftTeamLogo = (
                                    draftCompleted
                                    && sortMode === SORT_MODES.DRAFT_ORDER
                                    && draftTeamLogo
                                );
                                return (
                                    <button
                                        key={player.player_id}
                                        type="button"
                                        className={[
                                            "draft-hub-player-row",
                                            isActive ? "draft-hub-player-row--active" : "",
                                        ].filter(Boolean).join(" ")}
                                        onClick={() => setSelectedPlayerId(player.player_id)}
                                    >
                                        <span className="draft-hub-player-rank">
                                            {getListRank(player, sortMode, draftYear, draftCompleted)}
                                        </span>
                                        {showDraftTeamLogo && (
                                            <img
                                                src={draftTeamLogo}
                                                alt=""
                                                className="draft-hub-player-team-logo"
                                            />
                                        )}
                                        <Avatar
                                            src={player.player_image_url || undefined}
                                            alt={`${player.first_name} ${player.last_name}`}
                                            className="draft-hub-player-avatar"
                                        >
                                            {player.first_name?.[0]}
                                        </Avatar>
                                        <span className="draft-hub-player-info">
                                            <span className="draft-hub-player-name">
                                                {player.first_name} {player.last_name}
                                            </span>
                                            {player.position && (
                                                <span className="draft-hub-player-pos">
                                                    {player.position}
                                                </span>
                                            )}
                                        </span>
                                    </button>
                                );
                            })}
                        </Box>
                    </Box>

                    <Box className="draft-hub-column draft-hub-column--player">
                        {loadingPlayer && !selectedPlayerData ? (
                            <Box className="draft-hub-empty">Loading player…</Box>
                        ) : (
                            <DraftHubPlayerPanel
                                playerId={selectedPlayerId}
                                playerData={selectedPlayerData}
                            />
                        )}
                    </Box>

                    <Box className="draft-hub-column draft-hub-column--sidebar">
                        <Typography component="div" className="draft-hub-column-title">
                            Community
                        </Typography>
                        <DraftHubRightPanel playerId={selectedPlayerId} />
                    </Box>
                </Box>
            </Box>
        </>
    );
}

export default DraftHub;
